import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { m } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import { containerVariants, viewPort } from 'utils/framer';
import KIAImage from '../ui/image/KIAImage';

export interface SlideData {
  model?: string;
  tagline?: string;
  image: {
    src: string;
    alt: string;
  };
  cta?: string;
}
function KiaHeroSlider({ slides }: { slides: SlideData[] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
    return () => {
      emblaApi
        .off('reInit', onInit)
        .off('reInit', onSelect)
        .off('select', onSelect);
    };
  }, [emblaApi, onInit, onSelect]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi],
  );
  return (
    <m.div
      initial="hidden"
      whileInView="visible"
      viewport={viewPort}
      variants={containerVariants}
      className="relative group overflow-hidden aspect-[478/319] max-w-screen-kia-lg mx-auto"
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y will-change-transform">
          {slides.map((slide, index) => (
            <div key={index} className="relative flex-[0_0_100%]">
              <KIAImage
                loading="eager"
                src={slide.image.src}
                alt={slide.image.alt}
                className="w-full object-contain"
              />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={scrollPrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-kia-grey/50 size-10 lg:size-12 grid place-content-center rounded-full hover:bg-kia-grey lg:opacity-0 lg:group-hover:opacity-100 transition-all"
        aria-label="Föregående slide"
      >
        <span className="sr-only">Föregående slide</span>
        <svg
          className="rotate-180"
          width="37"
          height="37"
          viewBox="0 0 37 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17 23L21.5 18.5L17 14"
            stroke="#000"
            strokeWidth="1.5"
          ></path>
        </svg>
      </button>

      <button
        onClick={scrollNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-kia-grey/50 size-10 lg:size-12 grid place-content-center rounded-full  hover:bg-kia-grey lg:opacity-0 lg:group-hover:opacity-100 transition-all"
        aria-label="Nästa slide"
      >
        <span className="sr-only">Nästa slide</span>
        <svg
          width="37"
          height="37"
          viewBox="0 0 37 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17 23L21.5 18.5L17 14"
            stroke="#000"
            strokeWidth="1.5"
          ></path>
        </svg>
      </button>

      <div className="absolute bottom-4 lg:bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 lg:gap-4">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`relative h-1 overflow-hidden transition-all duration-300 ${
              index === selectedIndex
                ? 'w-8 md:w-16 bg-kia-grey'
                : 'w-4 md:w-8 bg-gray-500/50 hover:bg-gray-400'
            }`}
            aria-label={`Gå till slide ${index + 1}`}
          ></button>
        ))}
      </div>
    </m.div>
  );
}

export default KiaHeroSlider;
